import React, { useState } from 'react';
import { Grid, Paper, Typography, Box, Tooltip, List, ListItem, ListItemIcon, ListItemText, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import StarIcon from '@material-ui/icons/Star';
import WarningIcon from '@material-ui/icons/Warning';
import AssessmentIcon from '@material-ui/icons/Assessment';
import PerformanceDoughnut from './PerformanceDoughnut';
import HorizontalBar from '../../../components/Charts/HorizontalBar';
import { useHistory } from 'react-router-dom';
import Table from '../../../theme/Table';
import Rating from '@material-ui/lab/Rating';
import Select from '../../../theme/CustomMUI/Select';
import vayzLogo from '../../../assets/images/vayz.png';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    height: '100%',
    boxShadow: '0 3px 10px rgb(0 0 0 / 0.1)',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column'
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  },
  analysisIcon: {
    width: '40px',
    height: '40px',
    objectFit: 'contain',
    marginRight: theme.spacing(1)
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    height: 200
  },
  feedbackItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '& svg': {
      marginRight: theme.spacing(1),
      fontSize: 20
    }
  },
  positiveIcon: {
    color: '#4caf50'
  },
  negativeIcon: {
    color: '#f44336'
  },
  feedbackText: {
    fontSize: '0.9rem',
    color: '#666'
  },
  ratingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: theme.spacing(2),
    textAlign: 'center',
    minHeight: 0
  },
  ratingValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: '#1976d2',
    textAlign: 'center'
  },
  customTooltip: {
    backgroundColor: 'white',
    color: 'black',
    padding: '10px',
    borderRadius: '10px',
    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
    maxWidth: '200px',
    '& .MuiTooltip-tooltip': {
      backgroundColor: 'white',
      color: 'black',
      padding: '10px',
      borderRadius: '10px',
      boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
      maxWidth: '200px',
    }
  },
  campaignCard: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0
    }
  },
  failureCause: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#fff3e0',
    borderRadius: '8px',
    borderLeft: `4px solid #ff9800`,
    marginBottom: theme.spacing(2),
  },
  warningIcon: {
    color: '#ff9800',
    marginRight: theme.spacing(1),
  },
  detailedReport: {
    marginTop: theme.spacing(2),
  },
  reportText: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    whiteSpace: 'pre-line',
    maxHeight: '400px',
    overflow: 'auto',
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
  },
  detailedReportButton: {
    backgroundColor: '#1976d2',
    color: 'white',
    fontWeight: 600,
    padding: theme.spacing(1, 2),
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#1565c0',
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(1),
    }
  },
  markdownContent: {
    fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    lineHeight: 1.6,
    color: '#333',
    maxHeight: '70vh',
    overflow: 'auto',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',

    '& h1': {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#1976d2',
      borderBottom: '3px solid #1976d2',
      paddingBottom: theme.spacing(1.5),
      marginBottom: theme.spacing(3),
      marginTop: theme.spacing(2),
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      justifyContent: 'center',
      pageBreakAfter: 'avoid',
      '&::before': {
        content: '"📊"',
        marginRight: theme.spacing(1),
        fontSize: '1.5rem'
      }
    },

    '& h2': {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#2c3e50',
      borderLeft: '4px solid #3498db',
      paddingLeft: theme.spacing(2),
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
      backgroundColor: '#f8f9fa',
      borderRadius: '0 8px 8px 0',
      pageBreakAfter: 'avoid',
    },

    '& h3': {
      fontSize: '1.2rem',
      fontWeight: 600,
      color: '#34495e',
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(1.5),
      borderLeft: '3px solid #e74c3c',
      pageBreakAfter: 'avoid',
    },

    '& p': {
      marginBottom: theme.spacing(1.5),
      lineHeight: 1.7,
      fontSize: '0.95rem',
      color: '#555',
      textAlign: 'justify',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
    },

    '& strong': {
      fontWeight: 600,
      color: '#2c3e50',
      fontSize: '0.95rem',
    },

    '& ul, & ol': {
      paddingLeft: theme.spacing(3),
      marginBottom: theme.spacing(2.5),
      marginTop: theme.spacing(1),
    },

    '& li': {
      marginBottom: theme.spacing(1.2),
      fontSize: '0.95rem',
      lineHeight: 1.7,
      color: '#555',
      position: 'relative',

      '&::marker': {
        color: '#3498db',
        fontWeight: 'bold'
      }
    },

    '& ul li': {
      paddingLeft: theme.spacing(1),

      '&::before': {
        content: '"▸"',
        color: '#3498db',
        fontWeight: 'bold',
        position: 'absolute',
        left: '-12px',
      }
    },

    '& hr': {
      border: 'none',
      height: '2px',
      background: 'linear-gradient(90deg, #3498db, #e74c3c)',
      margin: theme.spacing(3, 0),
      borderRadius: '1px',
    },

    '& blockquote': {
      borderLeft: '4px solid #f39c12',
      paddingLeft: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      margin: theme.spacing(2, 0),
      fontStyle: 'italic',
      color: '#7f8c8d',
      backgroundColor: '#fdf6e3',
      borderRadius: '0 8px 8px 0',
    },

    '& code': {
      backgroundColor: '#2c3e50',
      color: '#ecf0f1',
      padding: '3px 6px',
      borderRadius: '4px',
      fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
      fontSize: '0.85em',
      fontWeight: 500,
    },

    '& pre': {
      backgroundColor: '#2c3e50',
      color: '#ecf0f1',
      padding: theme.spacing(2),
      borderRadius: '8px',
      overflow: 'auto',
      marginBottom: theme.spacing(2),
      border: '1px solid #34495e',

      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
        color: '#ecf0f1',
      }
    },

    // Özel sınıflar için stiller
    '& .performance-score': {
      display: 'inline-block',
      backgroundColor: '#27ae60',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '0.85rem',
      fontWeight: 600,
      marginLeft: theme.spacing(1),
    },

    '& .performance-score.low': {
      backgroundColor: '#e74c3c',
    },

    '& .performance-score.medium': {
      backgroundColor: '#f39c12',
    },

    '& .staff-section': {
      backgroundColor: '#f8f9fa',
      border: '1px solid #e9ecef',
      borderRadius: '10px',
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },

    '& .highlight-box': {
      backgroundColor: '#e3f2fd',
      border: '1px solid #2196f3',
      borderLeft: '4px solid #2196f3',
      borderRadius: '8px',
      padding: theme.spacing(2.5),
      marginBottom: theme.spacing(2.5),
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
    },

    '& .warning-box': {
      backgroundColor: '#fff3e0',
      border: '1px solid #ff9800',
      borderLeft: '4px solid #ff9800',
      borderRadius: '8px',
      padding: theme.spacing(2.5),
      marginBottom: theme.spacing(2.5),
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
    },

    '& .success-box': {
      backgroundColor: '#e8f5e8',
      border: '1px solid #4caf50',
      borderLeft: '4px solid #4caf50',
      borderRadius: '8px',
      padding: theme.spacing(2.5),
      marginBottom: theme.spacing(2.5),
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
    },

    '& .staff-section': {
      backgroundColor: '#f8f9fa',
      border: '1px solid #e9ecef',
      borderLeft: '4px solid #6c757d',
      borderRadius: '8px',
      padding: theme.spacing(2.5),
      marginBottom: theme.spacing(3),
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },

    '& .no-break': {
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
    },

    '& .page-break-before': {
      pageBreakBefore: 'always',
    },

    '& .page-break-after': {
      pageBreakAfter: 'always',
    },
  },

  // Print styles for PDF
  '@media print': {
    '& *': {
      '-webkit-print-color-adjust': 'exact !important',
      'color-adjust': 'exact !important',
      'print-color-adjust': 'exact !important'
    },

    '&': {
      fontSize: '14px',
      lineHeight: 1.6,
      maxHeight: 'none',
      overflow: 'visible',
      padding: '0',
      margin: '0'
    },

    '& h1': {
      fontSize: '20px',
      marginBottom: '16px',
    },

    '& h2': {
      fontSize: '18px',
      marginTop: '20px',
      marginBottom: '12px',
    },

    '& h3': {
      fontSize: '16px',
      marginTop: '16px',
      marginBottom: '10px',
    },

    '& .staff-section, & .highlight-box, & .warning-box, & .success-box': {
      marginBottom: '12px',
      border: '1px solid #ddd',
      padding: '12px',
    },

    '& .performance-score': {
      border: '1px solid #333',
      display: 'inline-block',
      padding: '4px 8px',
    },

    '& ul, & ol': {
      margin: '8px 0',
      paddingLeft: '20px',
    },

    '& li': {
      margin: '2px 0',
      lineHeight: 1.4,
    },

    '& p': {
      margin: '6px 0',
    },

    '& strong': {
      margin: '4px 0',
      display: 'block',
    }
  }
}
));

// Basit markdown parser fonksiyonu
const parseMarkdown = (markdown) => {
  if (!markdown) return '';

  let html = markdown
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Headers (order matters - start with h4, h3, h2, h1)
    .replace(/^#### (.*$)/gim, '<h4>$1</h4>')
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    // Strikethrough
    .replace(/~~(.*?)~~/g, '<del>$1</del>')
    // Blockquotes
    .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
    // Horizontal rules
    .replace(/^---$/gim, '<hr>')
    .replace(/^\*\*\*$/gim, '<hr>')
    // Lists - numbered
    .replace(/^\d+\. (.*$)/gim, '<ol-item>$1</ol-item>')
    // Lists - unordered
    .replace(/^\* (.*$)/gim, '<ul-item>$1</ul-item>')
    .replace(/^\- (.*$)/gim, '<ul-item>$1</ul-item>')
    .replace(/^\+ (.*$)/gim, '<ul-item>$1</ul-item>');

  // Convert double line breaks to paragraph breaks
  html = html.replace(/\n\n/g, '</p><p>');

  // Convert single line breaks to <br>
  html = html.replace(/\n/g, '<br/>');

  // Wrap with paragraphs
  html = '<p>' + html + '</p>';

  // Fix list items - unordered (grup ardışık li elemanlarını)
  html = html.replace(/(<ul-item>.*?<\/ul-item>(<br\/>)*)+/gs, (match) => {
    const items = match.replace(/<ul-item>/g, '<li>').replace(/<\/ul-item>/g, '</li>').replace(/<br\/>/g, '');
    return '<ul>' + items + '</ul>';
  });

  // Fix list items - ordered (grup ardışık li elemanlarını)
  html = html.replace(/(<ol-item>.*?<\/ol-item>(<br\/>)*)+/gs, (match) => {
    const items = match.replace(/<ol-item>/g, '<li>').replace(/<\/ol-item>/g, '</li>').replace(/<br\/>/g, '');
    return '<ol>' + items + '</ol>';
  });

  // Clean up empty paragraphs
  html = html.replace(/<p><\/p>/g, '');

  // Fix nested lists and headers in paragraphs
  html = html.replace(/<p>(<h[1-6]>.*?<\/h[1-6]>)<\/p>/g, '$1');
  html = html.replace(/<p>(<ul>.*?<\/ul>)<\/p>/gs, '$1');
  html = html.replace(/<p>(<ol>.*?<\/ol>)<\/p>/gs, '$1');
  html = html.replace(/<p>(<blockquote>.*?<\/blockquote>)<\/p>/g, '$1');
  html = html.replace(/<p>(<hr>)<\/p>/g, '$1');
  html = html.replace(/<p>(<pre>.*?<\/pre>)<\/p>/gs, '$1');

  return html;
};

// Detaylı rapor oluşturma fonksiyonu
const generateDetailedReport = (data) => {
  if (!data) return 'Rapor verileri bulunamadı.';

  let report = `# Vayz AI Performans Analiz Raporu

**Analiz Tarihi:** ${new Date().toLocaleDateString('tr-TR')}

---

## 🎯 Genel Değerlendirme

<div class="highlight-box no-break">

${data.summary || 'Genel değerlendirme bilgisi mevcut değil.'}

</div>

`;

  // Başarısızlık nedeni varsa ekle
  if (data.failure_cause) {
    report += `## ⚠️ Başarısızlık Nedeni

<div class="warning-box no-break">

${data.failure_cause}

</div>

`;
  }

  // Performans raporu
  if (data.performance_report) {
    const performanceScore = parseFloat(data.performance_report.overall_performance_score) || 0;
    const getScoreClass = (score) => {
      if (score >= 80) return 'performance-score';
      if (score >= 60) return 'performance-score medium';
      return 'performance-score low';
    };

    report += `## 📈 Performans Analizi

<div class="highlight-box no-break">

**Genel Performans Puanı:** <span class="${getScoreClass(performanceScore)}">${performanceScore}</span>

</div>

`;

    // Personel analizi
    if (data.performance_report.staff_analysis && data.performance_report.staff_analysis.length > 0) {
      report += `### 👥 Personel Performans Analizi

`;
      data.performance_report.staff_analysis.forEach((staff, index) => {
        const staffScore = parseFloat(staff.performance?.score) || 0;
        const staffScoreClass = getScoreClass(staffScore * 20); // 5 üzerinden puanı 100'e çevir

        report += `<div class="staff-section no-break">

**${index + 1}. ${staff.full_name}** <span class="${staffScoreClass}">${staff.performance?.score || 'N/A'}</span>

**Kategori:** ${staff.performance?.category === 'top_performer' ? '🌟 Üst Düzey Performans' : '📊 Ortalama Performans'}

`;

        if (staff.performance?.key_insights?.strengths?.length > 0) {
          report += `**💪 Güçlü Yönler:**
`;
          staff.performance.key_insights.strengths.forEach(strength => {
            report += `- ${strength}
`;
          });
        }

        if (staff.performance?.key_insights?.weaknesses?.length > 0) {
          report += `
**🎯 Gelişim Alanları:**
`;
          staff.performance.key_insights.weaknesses.forEach(weakness => {
            report += `- ${weakness}
`;
          });
        }

        if (staff.performance?.key_insights?.development_plan?.length > 0) {
          report += `
**🚀 Gelişim Planı:**
`;
          staff.performance.key_insights.development_plan.forEach(plan => {
            report += `- ${plan}
`;
          });
        }

        report += `
</div>

`;
      });
    }
  }

  // Hizmet analizi
  if (data.service_analysis?.top_services && data.service_analysis.top_services.length > 0) {
    report += `<div class="page-break-before"></div>

## 🏆 En İyi Performans Gösteren Hizmetler

<div class="success-box no-break">

`;
    data.service_analysis.top_services.forEach((service, index) => {
      const medals = ['🥇', '🥈', '🥉'];
      const medal = medals[index] || '🏅';
      report += `${medal} **${service.service_name}**

`;
    });
    report += `
</div>

`;
  }

  // Müşteri geri bildirimleri
  if (data.customer_feedback?.feedback_analysis) {
    const feedback = data.customer_feedback.feedback_analysis;
    const sentimentScore = feedback.sentiment_score !== "Mevcut veri yok" ?
      (parseFloat(feedback.sentiment_score) / 2).toFixed(1) : 'Veri mevcut değil';

    report += `## 💬 Müşteri Geri Bildirim Analizi

<div class="highlight-box no-break">

**Memnuniyet Puanı:** ⭐ **${sentimentScore}/5.0**

</div>

`;

    if (feedback.positive_themes && feedback.positive_themes.length > 0) {
      report += `### ✅ Olumlu Geri Bildirimler

<div class="success-box no-break">

`;
      feedback.positive_themes.forEach(theme => {
        report += `- 👍 ${theme}
`;
      });
      report += `
</div>

`;
    }

    if (feedback.negative_themes && feedback.negative_themes.length > 0) {
      report += `### 🔧 Gelişim Gerektiren Alanlar

<div class="warning-box no-break">

`;
      feedback.negative_themes.forEach(theme => {
        report += `- 🔍 ${theme}
`;
      });
      report += `
</div>

`;
    }
  }

  // Kampanya önerileri
  if (data.campaign_suggestions && data.campaign_suggestions.length > 0) {
    report += `## 🎯 Kampanya Önerileri

`;
    data.campaign_suggestions.forEach((campaign, index) => {
      report += `<div class="highlight-box no-break">

### ${index + 1}. 🚀 ${campaign.campaign_name}

**📝 Açıklama:** ${campaign.description}

**👥 Hedef Kitle:** ${campaign.target_audience}

**💡 Gerekçe:** ${campaign.data_driven_rationale}

</div>

`;
    });
  }

  report += `<div class="page-break-before"></div>

---

<div class="success-box no-break">

🤖 *Bu rapor Vayz AI tarafından otomatik olarak oluşturulmuştur.*

📅 *Rapor Tarihi: ${new Date().toLocaleString('tr-TR')}*

</div>`;

  return report;
};

const PerformanceReport = ({ data, analysisRecords, selectedRecordId, onAnalysisChange }) => {
  const classes = useStyles();
  const history = useHistory();
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const handleRowClick = (staffId) => {
    history.push(`/settings/employees/detail/${staffId}`);
  };

  const handleOpenReportDialog = () => {
    setReportDialogOpen(true);
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
  };


  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const element = document.getElementById('detailed-report-content');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Vayz Detaylı Analiz Raporu</title>
          <meta charset="UTF-8">
          <style>
            @page { 
              size: A4; 
              margin: 20mm 15mm; 
              orphans: 3;
              widows: 3;
              @top-left { content: none; }
              @top-center { content: none; }
              @top-right { content: none; }
              @bottom-left { content: none; }
              @bottom-center { content: none; }
              @bottom-right { content: none; }
            }
            
            @media print {
              @page {
                margin: 20mm 15mm;
                size: A4;
              }
              
              /* Header ve footer gizle */
              header, footer, .no-print {
                display: none !important;
              }
              
              /* Tarayıcı header/footer gizle */
              html, body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
            
            * {
              box-sizing: border-box;
            }
            
                         body { 
               font-family: 'Segoe UI', Arial, sans-serif; 
               margin: 0;
               padding: 0;
               font-size: 12px;
               line-height: 1.6;
               color: #333;
               max-width: 100%;
               overflow-wrap: break-word;
               word-wrap: break-word;
             }
            
            .container {
              width: 100%;
              max-width: 100%;
              margin: 0 auto;
              padding: 0;
            }
            
                         h1 { 
               color: #1976d2; 
               font-size: 20px; 
               font-weight: bold;
               margin: 20px 0 15px 0;
               page-break-after: avoid;
               text-align: center;
               border-bottom: 2px solid #1976d2;
               padding-bottom: 12px;
             }
            
                         h2 { 
               color: #2c3e50; 
               font-size: 16px; 
               font-weight: bold;
               margin: 25px 0 12px 0;
               page-break-after: avoid;
               border-left: 4px solid #3498db;
               padding-left: 15px;
               background-color: #f8f9fa;
               padding: 12px 0 12px 20px;
             }
            
            h3 { 
              color: #34495e; 
              font-size: 12px; 
              font-weight: bold;
              margin: 15px 0 8px 0;
              page-break-after: avoid;
              border-left: 3px solid #e74c3c;
              padding-left: 8px;
            }
            
                         p { 
               margin: 8px 0; 
               line-height: 1.7;
               text-align: justify;
             }
            
                         .highlight-box, .warning-box, .success-box, .staff-section {
               border: 1px solid #ddd;
               padding: 16px;
               margin: 12px 0;
               border-radius: 8px;
               page-break-inside: avoid;
               break-inside: avoid;
               orphans: 3;
               widows: 3;
               width: 100%;
               box-sizing: border-box;
             }
            
            .highlight-box { 
              background-color: #e3f2fd; 
              border-left: 4px solid #2196f3;
            }
            
            .warning-box { 
              background-color: #fff3e0; 
              border-left: 4px solid #ff9800;
            }
            
            .success-box { 
              background-color: #e8f5e8; 
              border-left: 4px solid #4caf50;
            }
            
            .staff-section { 
              background-color: #f8f9fa; 
              border-left: 4px solid #6c757d;
              margin-bottom: 15px;
            }
            
            .performance-score {
              background-color: #27ae60;
              color: white;
              padding: 3px 8px;
              border-radius: 12px;
              font-size: 10px;
              font-weight: bold;
              display: inline-block;
              margin-left: 5px;
            }
            
            .performance-score.medium { 
              background-color: #f39c12; 
            }
            
            .performance-score.low { 
              background-color: #e74c3c; 
            }
            
                         ul, ol { 
               margin: 10px 0; 
               padding-left: 22px; 
             }
            
            li { 
              margin: 2px 0; 
              line-height: 1.3; 
              page-break-inside: avoid;
            }
            
            strong { 
              font-weight: bold;
              display: inline;
              margin: 0;
            }
            
            .no-break {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            .page-break-before {
              page-break-before: always;
            }
            
            .page-break-after {
              page-break-after: always;
            }
            
            /* Responsive adjustments */
            @media print {
              body { font-size: 10px; }
              h1 { font-size: 16px; }
              h2 { font-size: 13px; }
              h3 { font-size: 11px; }
              .highlight-box, .warning-box, .success-box, .staff-section {
                padding: 8px;
                margin: 6px 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            ${element.innerHTML}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Personel performans tablosu için veri hazırlama
  const staffTableData = data?.performance_report?.staff_analysis?.map(staff => ({
    staff_id: staff.staff_id,
    full_name: staff.full_name,
    score: staff.performance.score,
    category: staff.performance.category === 'top_performer' ? 'Üst Düzey' : 'Ortalama',
    strengths: (
      <Tooltip
        placement="bottom"
        classes={{ tooltip: classes.customTooltip }}
        title={
          <div>
            {staff?.performance?.key_insights?.strengths?.map((strength, index) => (
              <Typography key={index} variant="body2" color="textSecondary">
                {strength}
              </Typography>
            ))}
          </div>
        }
      >
        <div>
          <Typography variant="body2" color="textSecondary">
            {staff?.performance?.key_insights?.strengths?.[0] || 'Güçlü Yön bulunmuyor'}
          </Typography>
        </div>
      </Tooltip>
    ),
    weaknesses: (
      <Tooltip
        placement="bottom"
        classes={{ tooltip: classes.customTooltip }}
        title={
          <div>
            {staff?.performance?.key_insights?.weaknesses?.map((weakness, index) => (
              <Typography key={index} variant="body2" color="textSecondary">
                {weakness}
              </Typography>
            ))}
          </div>
        }
      >
        <div>
          <Typography variant="body2" color="textSecondary">
            {staff?.performance?.key_insights?.weaknesses?.[0] || 'Zayıflık bulunmuyor'}
          </Typography>
        </div>
      </Tooltip>
    ),
    development_plan: (
      <Tooltip
        placement="bottom"
        classes={{ tooltip: classes.customTooltip }}
        title={
          <div>
            {staff?.performance?.key_insights?.development_plan?.map((plan, index) => (
              <Typography key={index} variant="body2" color="textSecondary">
                {plan}
              </Typography>
            ))}
          </div>
        }
      >
        <div>
          <Typography variant="body2" color="textSecondary">
            {staff?.performance?.key_insights?.development_plan?.[0] || 'Gelişim planı bulunmuyor'}
          </Typography>
        </div>
      </Tooltip>
    )
  })) || [];

  const tableHeadings = {
    staff_id: { label: "Personel ID" },
    full_name: { label: "İsim Soyisim" },
    score: { label: "Performans Puanı" },
    category: { label: "Kategori" },
    strengths: { label: "Güçlü Yönler" },
    weaknesses: { label: "Zayıflıklar" },
    development_plan: { label: "Gelişim Planı" }
  };

  return (
    <Box>
      {/* Özet Bölümü */}
      <Paper className={classes.paper} style={{ marginBottom: 24 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="h5" className={classes.title}>
              <img src={vayzLogo} alt="Vayz Logo" className={classes.analysisIcon} />
              Vayz'ın Genel Değerlendirmesi
            </Typography>
            <Typography variant="body1">
              {data?.summary}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" ml={2}>
            <Button
              variant="contained"
              className={classes.detailedReportButton}
              startIcon={<AssessmentIcon />}
              onClick={handleOpenReportDialog}
              style={{ height: '40px', minHeight: '40px' }}
            >
              Detaylı Rapor
            </Button>
            <Box width="300px" ml={2}>
              <Select
                label="Analiz Tarihi"
                items={analysisRecords}
                selected={selectedRecordId}
                labelKey="label"
                className={"mt-1"}
                valueKey="value"
                handler={onAnalysisChange}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Başarısızlık Nedeni */}
      {data?.failure_cause && (
        <Paper className={classes.paper} style={{ marginBottom: 24 }}>
          <div className={classes.failureCause}>
            <WarningIcon className={classes.warningIcon} />
            <Box>
              <Typography variant="h6" gutterBottom>
                Başarısızlık Nedeni
              </Typography>
              <Typography variant="body1">
                {data.failure_cause}
              </Typography>
            </Box>
          </div>
        </Paper>
      )}

      {/* Detaylı Rapor */}
      {data?.report && (
        <Paper className={classes.paper} style={{ marginBottom: 24 }}>
          <Typography variant="h6" gutterBottom>
            Detaylı Analiz Raporu
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Vayz AI tarafından oluşturulan kapsamlı analiz raporuna yukarıdaki "Detaylı Rapor" butonundan ulaşabilirsiniz.
            Rapor, işletmenizin performansına dair detaylı bulgular, öneriler ve gelişim planları içermektedir.
          </Typography>
        </Paper>
      )}

      <Grid container spacing={4}>
        {/* Genel Performans Puanı */}
        <Grid item xs={12} md={4}>
          <Paper className={classes.paper}>
            <Typography variant="h6" align="center" gutterBottom>
              Genel Performans Puanı
            </Typography>
            <div className={classes.chartContainer}>
              <PerformanceDoughnut
                data={{
                  datasets: [{
                    data: [
                      parseFloat(data?.performance_report?.overall_performance_score) || 0,
                      100 - (parseFloat(data?.performance_report?.overall_performance_score) || 0)
                    ],
                    backgroundColor: ['#1976d2', '#e0e0e0']
                  }]
                }}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%"
                }}
              />
            </div>
            <Typography variant="h4" align="center" color="primary">
              {data?.performance_report?.overall_performance_score}
            </Typography>
          </Paper>
        </Grid>

        {/* En İyi Hizmetler */}
        <Grid item xs={12} md={4}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom>
              En İyi Hizmetler
            </Typography>
            <div style={{ marginTop: 20 }}>
              {data?.service_analysis?.top_services?.map((service, index) => (
                <HorizontalBar
                  key={service.service_id}
                  title={service.service_name}
                  width={`${95 - (index * 5)}%`}
                  background="rgba(25, 118, 210, 0.8)"
                  label={`${95 - (index * 5)}%`}
                />
              ))}
            </div>
          </Paper>
        </Grid>

        {/* Kampanya Önerileri */}
        <Grid item xs={12} md={4}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom>
              Kampanya Önerileri
            </Typography>
            <div>
              {data?.campaign_suggestions?.map((campaign, index) => (
                <Paper key={index} className={classes.campaignCard} style={{ padding: 16 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {campaign.campaign_name}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {campaign.description}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Hedef Kitle: {campaign.target_audience}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Gerekçe: {campaign.data_driven_rationale}
                  </Typography>
                </Paper>
              ))}
            </div>
          </Paper>
        </Grid>

        {/* Müşteri Memnuniyeti */}
        <Grid item xs={12} md={4} style={{ marginTop: 32 }}>
          <Paper className={classes.paper}>
            <Typography variant="h6" align="center" gutterBottom>
              Müşteri Memnuniyeti
            </Typography>
            <div className={classes.ratingContainer}>
              <Typography className={classes.ratingValue}>
                {data?.customer_feedback?.feedback_analysis?.sentiment_score !== "Mevcut veri yok" ?
                  (parseFloat(data.customer_feedback.feedback_analysis.sentiment_score) / 2).toFixed(0) : '0'}/5
              </Typography>
              <Rating
                value={data?.customer_feedback?.feedback_analysis?.sentiment_score ?
                  parseFloat(data.customer_feedback.feedback_analysis.sentiment_score) / 2 : 0}
                precision={0.5}
                readOnly
                size="large"
                max={5}
                style={{ display: 'flex', justifyContent: 'center' }}
              />
            </div>
          </Paper>
        </Grid>

        {/* Olumlu Temalar */}
        <Grid item xs={12} md={4} style={{ marginTop: 32 }}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom>
              Olumlu Yönler
            </Typography>
            <List>
              {data?.customer_feedback?.feedback_analysis?.positive_themes?.map((theme, index) => (
                <ListItem key={index} className={classes.feedbackItem}>
                  <ListItemIcon>
                    <CheckCircleIcon className={classes.positiveIcon} />
                  </ListItemIcon>
                  <ListItemText primary={theme} className={classes.feedbackText} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Olumsuz Temalar */}
        <Grid item xs={12} md={4} style={{ marginTop: 32 }}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom>
              Olumsuz Yönler
            </Typography>
            <List>
              {data?.customer_feedback?.feedback_analysis?.negative_themes?.map((theme, index) => (
                <ListItem key={index} className={classes.feedbackItem}>
                  <ListItemIcon>
                    <CancelIcon className={classes.negativeIcon} />
                  </ListItemIcon>
                  <ListItemText primary={theme} className={classes.feedbackText} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Personel Performans Tablosu */}
        <Grid item xs={12} style={{ marginTop: 32 }}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom>
              Personel Performans Analizi
            </Typography>
            <Table
              headings={tableHeadings}
              rows={staffTableData}
              loaded={true}
              onRowClick={handleRowClick}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Detaylı Rapor Modal */}
      <Dialog
        open={reportDialogOpen}
        onClose={handleCloseReportDialog}
        maxWidth="lg"
        fullWidth
        aria-labelledby="report-dialog-title"
        PaperProps={{
          style: {
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle id="report-dialog-title" style={{
          backgroundColor: '#1976d2',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          fontSize: '1.2rem',
          fontWeight: 600
        }}>
          <AssessmentIcon style={{ marginRight: 8 }} />
          📊 Detaylı Analiz Raporu
        </DialogTitle>
        <DialogContent>
          <div id="detailed-report-content" className={classes.markdownContent}>
            <div
              dangerouslySetInnerHTML={{
                __html: parseMarkdown(generateDetailedReport(data))
              }}
            />
          </div>
        </DialogContent>
        <DialogActions style={{
          padding: '16px 24px',
          backgroundColor: '#f5f5f5',
          borderTop: '1px solid #e0e0e0',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }}>
          <Typography variant="body2" style={{
            color: '#666',
            marginBottom: '12px',
            fontStyle: 'italic'
          }}>
            💡 İpucu: Raporu PDF olarak kaydetmek için "Yazdır" butonuna tıklayın ve açılan pencerede "PDF olarak kaydet" seçeneğini seçin.
          </Typography>
          <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Button
              onClick={handlePrint}
              color="primary"
              variant="contained"
              style={{
                backgroundColor: '#4caf50',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#45a049'
                }
              }}
            >
              🖨️ Yazdır / PDF Kaydet
            </Button>
            <Button
              onClick={handleCloseReportDialog}
              color="secondary"
              variant="outlined"
              style={{
                fontWeight: 600,
                marginLeft: '8px'
              }}
            >
              ❌ Kapat
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerformanceReport; 